import { useState } from 'react'
import * as Icons from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import type { CampaignDetail } from '../types'

interface CampaignTableProps {
  campaigns: CampaignDetail[]
  loading?: boolean
}

// Format currency
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

type SortField = 'name' | 'spend' | 'revenue' | 'roas' | 'ctr' | 'cpc'
type SortOrder = 'asc' | 'desc'

export function CampaignTable({ campaigns, loading }: CampaignTableProps) {
  const [sortField, setSortField] = useState<SortField>('spend')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [searchQuery, setSearchQuery] = useState('')

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('desc')
    }
  }

  // Filter and sort campaigns
  const filteredCampaigns = campaigns
    .filter((c) => (c.campaign_name || '').toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      let aValue: number | string
      let bValue: number | string

      switch (sortField) {
        case 'name':
          aValue = (a.campaign_name || '').toLowerCase()
          bValue = (b.campaign_name || '').toLowerCase()
          break
        case 'spend':
          aValue = a.spend
          bValue = b.spend
          break
        case 'revenue':
          aValue = a.revenue
          bValue = b.revenue
          break
        case 'roas':
          aValue = a.roas
          bValue = b.roas
          break
        case 'ctr':
          aValue = a.ctr
          bValue = b.ctr
          break
        case 'cpc':
          aValue = a.cpc
          bValue = b.cpc
          break
        default:
          return 0
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
      }
      return sortOrder === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number)
    })

  // Sort indicator
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null
    return sortOrder === 'asc' ? (
      <Icons.ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <Icons.ChevronDown className="w-4 h-4 inline ml-1" />
    )
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-tf-bg-card rounded-xl border border-tf-border overflow-hidden">
        <div className="px-6 py-4 border-b border-tf-border">
          <div className="h-5 bg-tf-bg-hover rounded w-40 animate-pulse" />
        </div>
        <div className="p-6 space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-12 bg-tf-bg-hover rounded animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  // Empty state
  if (campaigns.length === 0) {
    return (
      <div className="bg-tf-bg-card rounded-xl border border-tf-border p-12 text-center">
        <Icons.BarChart3 className="w-12 h-12 text-tf-text-muted mx-auto mb-4" />
        <h4 className="text-base font-medium text-tf-text mb-2">No Campaign Data</h4>
        <p className="text-sm text-tf-text-muted">
          Connect your Meta account and sync data to view campaigns
        </p>
      </div>
    )
  }

  return (
    <div className="bg-tf-bg-card rounded-xl border border-tf-border overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-tf-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold text-tf-text">Campaign Performance</h3>
          <span className="text-sm text-tf-text-muted">{campaigns.length} campaigns</span>
        </div>
        <div className="relative">
          <Icons.Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-tf-text-muted" />
          <input
            type="text"
            placeholder="Search campaigns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm border border-tf-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none w-64"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-tf-bg-muted">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider cursor-pointer hover:text-tf-text"
                onClick={() => handleSort('name')}
              >
                Campaign
                <SortIcon field="name" />
              </th>
              <th className="px-6 py-3 text-center text-xs font-semibold text-tf-text-muted uppercase tracking-wider">
                Status
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-semibold text-tf-text-muted uppercase tracking-wider cursor-pointer hover:text-tf-text"
                onClick={() => handleSort('spend')}
              >
                Spend
                <SortIcon field="spend" />
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-semibold text-tf-text-muted uppercase tracking-wider cursor-pointer hover:text-tf-text"
                onClick={() => handleSort('revenue')}
              >
                Revenue
                <SortIcon field="revenue" />
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-semibold text-tf-text-muted uppercase tracking-wider cursor-pointer hover:text-tf-text"
                onClick={() => handleSort('roas')}
              >
                ROAS
                <SortIcon field="roas" />
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-semibold text-tf-text-muted uppercase tracking-wider cursor-pointer hover:text-tf-text"
                onClick={() => handleSort('ctr')}
              >
                CTR
                <SortIcon field="ctr" />
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-semibold text-tf-text-muted uppercase tracking-wider cursor-pointer hover:text-tf-text"
                onClick={() => handleSort('cpc')}
              >
                CPC
                <SortIcon field="cpc" />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-tf-border">
            {filteredCampaigns.map((campaign) => (
              <tr key={campaign.campaign_id} className="hover:bg-tf-bg-muted transition-colors">
                <td className="px-6 py-4">
                  <div className="max-w-xs">
                    <p className="text-sm font-medium text-tf-text truncate">{campaign.campaign_name || 'Unnamed Campaign'}</p>
                    <p className="text-xs text-tf-text-muted truncate">{campaign.objective}</p>
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium',
                      campaign.status === 'ACTIVE'
                        ? 'bg-tf-feedback-success-bg text-tf-feedback-success-text'
                        : campaign.status === 'PAUSED'
                          ? 'bg-tf-feedback-warning-bg text-tf-feedback-warning-text'
                          : 'bg-tf-bg-hover text-tf-text-muted'
                    )}
                  >
                    {campaign.status === 'ACTIVE' && <Icons.Circle className="w-2 h-2 fill-current" />}
                    {campaign.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-tf-text text-right font-medium">
                  {formatCurrency(campaign.spend || 0)}
                </td>
                <td className="px-6 py-4 text-sm text-tf-text text-right">
                  {formatCurrency(campaign.revenue || 0)}
                </td>
                <td className="px-6 py-4 text-sm text-right">
                  <span className={cn('font-semibold', (campaign.roas || 0) >= 1 ? 'text-tf-feedback-success-text' : 'text-tf-feedback-error-text')}>
                    {(campaign.roas || 0).toFixed(2)}x
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-tf-text text-right">{(campaign.ctr || 0).toFixed(2)}%</td>
                <td className="px-6 py-4 text-sm text-tf-text text-right">{formatCurrency(campaign.cpc || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty search results */}
      {filteredCampaigns.length === 0 && searchQuery && (
        <div className="p-12 text-center">
          <Icons.Search className="w-8 h-8 text-tf-text-muted mx-auto mb-3" />
          <p className="text-sm text-tf-text-muted">No campaigns match "{searchQuery}"</p>
        </div>
      )}
    </div>
  )
}

export default CampaignTable
